import { Vec3, vec3, sub, scale, cross, dot, length, lengthSq } from '../math/vec3'
import { clamp } from '../math/scalar'

/**
 * Elementi orbitali classici.
 * Riferimento: polo +Z, piano equatoriale XY, direzione di riferimento +X.
 */
export interface OrbitElements {
  /** semiasse maggiore, m. Negativo per le iperboli. */
  readonly sma: number
  readonly ecc: number
  /** inclinazione, rad */
  readonly inc: number
  /** ascensione retta del nodo ascendente, rad */
  readonly raan: number
  /** argomento del periapside, rad */
  readonly argPe: number
  /** anomalia vera, rad */
  readonly trueAnomaly: number
  readonly mu: number
  /** istante a cui si riferisce l'anomalia vera, s */
  readonly epoch: number
}

const TAU = Math.PI * 2
const wrap = (a: number): number => ((a % TAU) + TAU) % TAU

export const specificEnergy = (position: Vec3, velocity: Vec3, mu: number): number =>
  lengthSq(velocity) / 2 - mu / length(position)

export function elementsFromState(position: Vec3, velocity: Vec3, mu: number, epoch: number): OrbitElements {
  const r = length(position)
  const v2 = lengthSq(velocity)
  const h = cross(position, velocity)
  const hLen = length(h)

  // vettore eccentricita: e = ((v² − μ/r) r − (r·v) v)/μ
  const eVec = scale(
    sub(scale(position, v2 - mu / r), scale(velocity, dot(position, velocity))),
    1 / mu,
  )
  const ecc = length(eVec)

  const energy = v2 / 2 - mu / r
  // per e = 1 esatta il semiasse diverge: caso di misura nulla, lo evitiamo
  const sma = Math.abs(ecc - 1) < 1e-12 ? Infinity : -mu / (2 * energy)

  const inc = Math.acos(clamp(h.z / hLen, -1, 1))

  // vettore nodo: n = ẑ × h
  const n = vec3(-h.y, h.x, 0)
  const nLen = length(n)
  const equatoriale = nLen < 1e-9 * hLen
  const circolare = ecc < 1e-9

  let raan = 0
  if (!equatoriale) {
    raan = Math.acos(clamp(n.x / nLen, -1, 1))
    if (n.y < 0) raan = TAU - raan
  }

  let argPe = 0
  if (!circolare) {
    if (equatoriale) {
      // longitudine del periapside, invertita se l'orbita e retrograda
      argPe = wrap(Math.atan2(eVec.y, eVec.x) * (h.z < 0 ? -1 : 1))
    } else {
      argPe = Math.acos(clamp(dot(n, eVec) / (nLen * ecc), -1, 1))
      if (eVec.z < 0) argPe = TAU - argPe
    }
  }

  let trueAnomaly: number
  if (circolare) {
    const riferimento = equatoriale ? vec3(1, 0, 0) : n
    const denom = equatoriale ? 1 : nLen
    trueAnomaly = Math.acos(clamp(dot(riferimento, position) / (denom * r), -1, 1))
    const proiezione = equatoriale ? position.y * (h.z < 0 ? -1 : 1) : position.z
    if (proiezione < 0) trueAnomaly = TAU - trueAnomaly
  } else {
    trueAnomaly = Math.acos(clamp(dot(eVec, position) / (ecc * r), -1, 1))
    if (dot(position, velocity) < 0) trueAnomaly = TAU - trueAnomaly
  }

  return { sma, ecc, inc, raan, argPe, trueAnomaly: wrap(trueAnomaly), mu, epoch }
}

/** Semilato retto p = a(1 − e²), valido anche per le iperboli. */
export const semiLatusRectum = (el: OrbitElements): number =>
  el.sma * (1 - el.ecc * el.ecc)

export function stateFromElements(el: OrbitElements): { position: Vec3; velocity: Vec3 } {
  const p = semiLatusRectum(el)
  const nu = el.trueAnomaly
  const cosNu = Math.cos(nu)
  const sinNu = Math.sin(nu)
  const r = p / (1 + el.ecc * cosNu)

  // sistema perifocale: X verso il periapside, Y a 90° nel verso del moto
  const rPf = vec3(r * cosNu, r * sinNu, 0)
  const k = Math.sqrt(el.mu / p)
  const vPf = vec3(-k * sinNu, k * (el.ecc + cosNu), 0)

  return { position: perifocalToInertial(rPf, el), velocity: perifocalToInertial(vPf, el) }
}

/** Rotazione perifocale → inerziale: Rz(Ω) · Rx(i) · Rz(ω). */
function perifocalToInertial(v: Vec3, el: OrbitElements): Vec3 {
  const cO = Math.cos(el.raan), sO = Math.sin(el.raan)
  const ci = Math.cos(el.inc), si = Math.sin(el.inc)
  const cw = Math.cos(el.argPe), sw = Math.sin(el.argPe)

  const x = v.x * (cO * cw - sO * sw * ci) + v.y * (-cO * sw - sO * cw * ci)
  const y = v.x * (sO * cw + cO * sw * ci) + v.y * (-sO * sw + cO * cw * ci)
  const z = v.x * (sw * si) + v.y * (cw * si)
  return vec3(x, y, z)
}

export const isClosed = (el: OrbitElements): boolean => el.ecc < 1 && el.sma > 0

export const orbitalPeriod = (el: OrbitElements): number =>
  isClosed(el) ? TAU * Math.sqrt(el.sma ** 3 / el.mu) : Infinity

export const periapsisRadius = (el: OrbitElements): number => el.sma * (1 - el.ecc)

export const apoapsisRadius = (el: OrbitElements): number =>
  isClosed(el) ? el.sma * (1 + el.ecc) : Infinity
